export type StepStatus = "completed" | "current" | "upcoming";

export interface StepV2 {
  label: string;
  description: string;
  formattedDescription?: string;
  status: StepStatus;
}

export interface DriverInfoV2 {
  avatar: string;
  name: string;
  truck: string;
  plate: string;
}

export interface TransportTimelineV2Props {
  steps: StepV2[];
  driver?: DriverInfoV2;
}

export default function TransportTimelineV2({
  steps,
  driver,
}: TransportTimelineV2Props) {
  return (
    <div className="flex">
      <ul
        role="list"
        className="flex flex-col items-start md:pr-10 pr-2 space-y-8"
      >
        {steps.map((step, idx) => {
          const isLast = idx === steps.length - 1;
          const isCompleted = step.status === "completed";
          const isCurrent = step.status === "current";

          const circleBase =
            "w-10 h-10 flex items-center justify-center rounded-full border-2 text-base font-semibold shrink-0";
          const circleCompleted =
            "border-gray-400 bg-n-gray-ultra-light text-n-green";
          const circleCurrent = "border-n-green bg-n-secondary text-n-green";
          const circleUpcoming = "border-gray-300 bg-white text-gray-400";

          return (
            <li key={idx} className="relative flex gap-4">
              {!isLast && (
                <span
                  aria-hidden
                  className="absolute left-5 top-10 -bottom-8 border-l-2 border-dashed border-gray-300"
                />
              )}

              <span
                className={`${circleBase} ${
                  isCompleted
                    ? circleCompleted
                    : isCurrent
                      ? circleCurrent
                      : circleUpcoming
                }`}
              >
                {isCompleted ? (
                  <img
                    src="/icons/customer/ok.svg"
                    alt="Concluído"
                    className="w-5 h-5"
                  />
                ) : (
                  idx + 1
                )}
              </span>

              <div
                className={`flex-1 ${
                  isCurrent
                    ? "px-5 py-3 rounded-2xl border border-gray-300 bg-white"
                    : "pt-2"
                }`}
              >
                <p
                  className={`font-semibold ${
                    isCompleted || isCurrent ? "text-n-green" : "text-gray-400"
                  }`}
                >
                  {step.label}
                </p>
                {step.formattedDescription ? (
                  <p
                    className={`text-sm leading-5 ${
                      isCompleted || isCurrent
                        ? "text-gray-700"
                        : "text-gray-300"
                    }`}
                    dangerouslySetInnerHTML={{
                      __html: step.formattedDescription,
                    }}
                  />
                ) : (
                  <p
                    className={`text-sm leading-5 ${
                      isCompleted || isCurrent
                        ? "text-gray-700"
                        : "text-gray-300"
                    }`}
                  >
                    {step.description}
                  </p>
                )}
              </div>
            </li>
          );
        })}
      </ul>

      {driver && (
        <div className="pl-8 border-l border-gray-200 space-y-6">
          <h3 className="font-semibold text-n-green">Motorista parceiro:</h3>

          <div className="flex items-center gap-4">
            <img
              src={driver.avatar}
              alt={driver.name}
              className="w-14 h-14 rounded-full object-cover"
            />
            <div>
              <p className="font-medium">{driver.name}</p>
              <p className="text-xs text-n-text-neutral">
                Motorista Autorizado
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm font-semibold">
            <span>Caminhão:</span>
            <span>Placa:</span>
            <span className="font-normal text-n-text-neutral">
              {driver.truck}
            </span>
            <span className="font-normal text-n-text-neutral">
              {driver.plate}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
